import { useTranslation } from "react-i18next";

import founderPhoto from "~/assets/rodrigo-gonzalez.jpg";

import { WashiTape } from "../CuadernoVivo/WashiTape";

type Proof = { title: string; desc: string };

const POSTIT_STYLES = [
  { rotate: "-1.5deg", bg: "#F6E3AE" },
  { rotate: "0.8deg", bg: "#EFE6C6" },
  { rotate: "-0.6deg", bg: "#F6E3AE" },
];

/** Redes del founder. Se usa en el perfil de desktop y en el bloque de mobile. */
function FounderSocials({ className }: { className?: string }) {
  return (
    <span className={className}>
      <a
        href="https://www.linkedin.com/in/gonzalezrod/"
        target="_blank"
        rel="noreferrer"
        aria-label="LinkedIn de Rodrigo González"
        data-cuelume-hover="tick"
        data-cuelume-press
        // -m-2.5 mantiene la separación visual entre íconos; el área táctil sube a 44px.
        className="-m-2.5 inline-flex h-11 w-11 items-center justify-center text-landing-muted transition-colors hover:text-landing-teal"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
        </svg>
      </a>
      <a
        href="https://x.com/gkgonzalez90"
        target="_blank"
        rel="noreferrer"
        aria-label="X de Rodrigo González"
        data-cuelume-hover="tick"
        data-cuelume-press
        // -m-2.5 mantiene la separación visual entre íconos; el área táctil sube a 44px.
        className="-m-2.5 inline-flex h-11 w-11 items-center justify-center text-landing-muted transition-colors hover:text-landing-teal"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z" />
        </svg>
      </a>
    </span>
  );
}

/** Rol del founder, con el logotipo inline. */
function FounderRole({ className }: { className?: string }) {
  return (
    <span className={className}>
      CEO &amp; Founder de{" "}
      <span className="font-display font-bold">
        <span className="text-landing-text">Academ</span>
        <span className="text-landing-red">IA</span>
      </span>
    </span>
  );
}

/**
 * Quiénes somos — credibility + warmth: we work with AI daily, that's why we can
 * explain it simply. The "hecho con IA, a la vista" proof lives on yellow post-its
 * (desk notes); closes with the mission as a handwritten-style italic quote.
 */
export function AboutLaunchSection() {
  const { t } = useTranslation();
  const proof = t("landing.launch.about.proof", { returnObjects: true }) as Proof[];
  const founderBio = t("landing.launch.about.founder.bio").trim();

  return (
    <section
      id="quienes-somos"
      className="landing-grain relative bg-landing-amber-light px-5 py-20 sm:px-8"
    >
      <div className="relative z-10 mx-auto max-w-[1100px]">
        <div className="mx-auto max-w-[700px] text-center">
          <span className="landing-eyebrow block font-bold text-landing-warning">
            {t("landing.launch.about.eyebrow")}
          </span>
          <h2 className="font-display mt-3 text-[clamp(24px,3.2vw,34px)] font-bold leading-tight tracking-tight text-landing-text">
            {t("landing.launch.about.title")}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-landing-muted">
            {t("landing.launch.about.lead")}
          </p>
        </div>
        <div className="mx-auto mt-8 grid max-w-[900px] gap-4 sm:grid-cols-3">
          {proof.map((p, i) => {
            const style = POSTIT_STYLES[i % POSTIT_STYLES.length];
            return (
              <div
                key={p.title}
                className="rounded-[3px] p-5 shadow-landing-md"
                style={{
                  transform: `rotate(${style.rotate})`,
                  background: style.bg,
                }}
              >
                <b className="font-display mb-1 block text-[16px] text-landing-text">{p.title}</b>
                <span className="text-[13px] text-[#5c5344]">{p.desc}</span>
              </div>
            );
          })}
        </div>
        {/* La visión del founder.
            DESKTOP: intacto — foto (con perfil debajo) a la izquierda, quote + bio a la derecha.
            MOBILE (solo `max-md:`): primero el quote a lo ancho, y debajo la foto chica a la
            izquierda con la mini-bio al lado; nombre/rol/redes siguen bajo la foto. */}
        <div className="mx-auto mt-12 grid max-w-[900px] items-start gap-8 max-md:grid-cols-[68px_1fr] max-md:gap-x-3.5 max-md:gap-y-4 md:grid-cols-[auto_1fr] md:gap-10">
          <figure className="relative m-0 mx-auto flex w-[200px] flex-col items-center max-md:col-start-1 max-md:row-start-2 max-md:mx-0 max-md:w-full">
            <span className="relative w-[180px] rotate-[-2deg] max-md:w-full">
              <WashiTape variant={3} className="max-md:hidden" />
              <img
                src={founderPhoto}
                alt="Rodrigo González, CEO y fundador de AcademIA"
                width={180}
                height={180}
                loading="lazy"
                className="w-full rounded-[10px] bg-landing-paper object-cover shadow-landing-lg"
              />
            </span>
            {/* El perfil, debajo de la foto. Sólo desktop: en mobile va a la derecha. */}
            <figcaption className="mt-5 flex flex-col items-center text-center max-md:hidden">
              <b className="font-display text-[17px] leading-tight text-landing-text">
                {t("landing.launch.about.founder.name")}
              </b>
              <FounderRole className="mt-0.5 text-[13px] font-semibold text-landing-muted" />
              <FounderSocials className="mt-2.5 flex items-center gap-3" />
            </figcaption>
          </figure>
          {/* Quote: a la derecha en desktop; en mobile pasa a la fila 1, a todo el ancho */}
          <blockquote className="relative m-0 max-md:col-span-2 max-md:col-start-1 max-md:row-start-1">
            <span
              aria-hidden="true"
              className="font-display absolute -left-1 -top-6 text-[64px] leading-none text-landing-red opacity-30"
            >
              “
            </span>
            <div className="relative flex flex-col gap-3">
              {(
                t("landing.launch.about.founder.quoteParagraphs", {
                  returnObjects: true,
                }) as string[]
              ).map((paragraph, index) => (
                <p
                  key={index}
                  className="font-display m-0 text-[clamp(15.5px,1.9vw,18px)] italic leading-relaxed text-landing-text"
                >
                  {paragraph}
                </p>
              ))}
            </div>
            {/* Bio en DESKTOP: debajo del quote. Es la copia que leen los lectores de
                pantalla. Sólo si hay bio: hasta ahora salía un "Lorem ipsum" en prod. La clave
                queda vacía hasta que Rodrigo escriba la real, y con ella vacía el párrafo no
                se renderiza (ni acá ni en mobile). */}
            {founderBio && (
              <p className="mt-4 max-w-[62ch] text-[13px] leading-relaxed text-landing-muted max-md:hidden">
                {founderBio}
              </p>
            )}
          </blockquote>

          {/* MOBILE, a la derecha de la foto: nombre + redes en una línea, el rol debajo
              y la bio al pie. aria-hidden: la copia accesible es la del figcaption/quote. */}
          {/* MOBILE — a la derecha del thumb, a su misma altura: nombre + redes y el rol.
              La bio va aparte, abajo y a TODO el ancho (fila 3). aria-hidden porque la
              copia accesible es la del figcaption/quote. */}
          <div
            aria-hidden="true"
            className="self-center max-md:col-start-2 max-md:row-start-2 md:hidden"
          >
            <div className="flex items-center gap-2.5">
              <b className="font-display text-[16px] leading-tight text-landing-text">
                {t("landing.launch.about.founder.name")}
              </b>
              <FounderSocials className="flex shrink-0 items-center gap-2.5" />
            </div>
            <FounderRole className="mt-1 block text-[12px] font-semibold leading-snug text-landing-muted" />
          </div>
          {founderBio && (
            <p
              aria-hidden="true"
              className="text-[12.5px] leading-relaxed text-landing-muted max-md:col-span-2 max-md:col-start-1 max-md:row-start-3 md:hidden"
            >
              {founderBio}
            </p>
          )}
        </div>

        <p className="font-display mx-auto mt-10 max-w-[620px] text-center text-[clamp(17px,2.4vw,21px)] italic text-landing-text">
          {t("landing.launch.about.missionPre")}
          <b className="not-italic text-landing-red">{t("landing.launch.about.missionEm")}</b>
        </p>
      </div>
    </section>
  );
}
